import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  Statistic,
  Progress,
  Table,
  Tag,
  Spin,
  Typography,
  Space,
  Avatar,
  List,
  Button,
  Empty,
  Tooltip,
  Select,
  Pagination
} from "antd";
import {
  LoadingOutlined,
  UserOutlined,
  BookOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  BarChartOutlined,
  RiseOutlined,
  ClusterOutlined,
  ArrowRightOutlined
} from "@ant-design/icons";
import PropTypes from 'prop-types';
import { useNavigate } from "react-router-dom";
import { getStyles } from "PlatForms/OrgLMS/Dashboards/Admin/LandingPage/Style";
import useProAdminDashboard from "../Hooks/useProAdminDashboard";
import { ProAdminHeader } from "./ProAdminHeader";
import { organizationService } from "../Services/OrganizationalService";

const { Title, Text } = Typography;
const { Option } = Select;

// Detail Item Component
const DetailItem = ({ label, value }) => (
  <Row gutter={16}>
    <Col span={8}>
      <Text strong>{label}:</Text>
    </Col>
    <Col span={16}>
      {typeof value === 'string' || typeof value === 'number' ? (
        <Text>{value}</Text>
      ) : (
        value
      )}
    </Col>
  </Row>
);

DetailItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.node
  ])
};

DetailItem.defaultProps = {
  value: 'N/A'
};

const ProAdminLandingPage = () => {
  const navigate = useNavigate();
  const [darkMode, setDarkMode] = useState(false);
  const { dashboardData, loading, error } = useProAdminDashboard();
  const [organizations, setOrganizations] = useState([]);
  const [orgLoading, setOrgLoading] = useState(true);
  const [orgFilter, setOrgFilter] = useState("all");
  console.log("?????????????????????Dashboard data of admin",dashboardData);
  // Pagination states
  const [courseCurrentPage, setCourseCurrentPage] = useState(1);
  const [coursePageSize, setCoursePageSize] = useState(5);
  const [enrollmentCurrentPage, setEnrollmentCurrentPage] = useState(1);
  const [enrollmentPageSize, setEnrollmentPageSize] = useState(5);

  const spinnerIcon = <LoadingOutlined style={{ fontSize: 48, color: "#1890ff" }} spin />;

  // Fetch organizations
  const fetchOrganizations = async () => {
  try {
    setOrgLoading(true);
    const data = await organizationService.getOrganizationList();

    console.log("Org API raw response:", data);

    const orgArray =
      data?.items ||
      data?.organizations ||
      data?.data ||
      (Array.isArray(data) ? data : []);

    setOrganizations(orgArray);
  } catch (error) {
    console.error('Error fetching organizations:', error);
    setOrganizations([]); // IMPORTANT
  } finally {
    setOrgLoading(false);
  }
};


  const handleViewOrganization = (organization) => {
    // Navigate to organization details page
    navigate(`/organization-detail/${organization.id}`, {
      state: { organization }
    });
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const safeData = {
    learnerCount: dashboardData?.learnerCount || 0,
    learnerMonthCount: dashboardData?.learnerMonthCount || 0,
    organizationCount: dashboardData?.organizationCount || 0,
    pendingOrgRequestCount: dashboardData?.pendingOrgRequestCount || 0,
    coursesCount: dashboardData?.coursesCount || 0,
    enrollmentCount: dashboardData?.enrollmentCount || 0,
    enrollmentMonthCount: dashboardData?.enrollmentMonthCount || 0,
    totalCertificates: dashboardData?.totalCertificates || 0,
    onlineLearnerCount: dashboardData?.onlineLearnerCount || 0,
    courseLEnrollCountsList: dashboardData?.courseEnrollmentCountsList || [],
    recentEnrollmentList: dashboardData?.recentEnrollmentList || [],
  };

  console.log("saf ", safeData);
  console.log("enroll", safeData.courseLEnrollCountsList);
  
  // Organization table columns - UPDATED with navigation button
  const orgColumns = [
    {
      title: 'Organization Name',
      dataIndex: 'name',
      key: 'name',
      render: (name) => (
        <Space>
          <ClusterOutlined style={{ color: '#1890ff' }} />
          <Text strong>{name}</Text>
        </Space>
      ),
    },
    {
      title: 'Location',
      key: 'location',
      render: (_, record) => (
        <Text type="secondary">
          {record.city || 'N/A'}, {record.state || 'N/A'}
        </Text>
      ),
    },
    {
      title: 'Specialty Area',
      dataIndex: 'specialtyAreaName',
      key: 'specialtyAreaName',
      render: (specialtyAreaName) => (
        <Text>{specialtyAreaName}</Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const statusConfig = {
          0: { color: 'orange', text: 'PENDING', icon: <ClockCircleOutlined /> },
          1: { color: 'green', text: 'APPROVED', icon: <CheckCircleOutlined /> }
        };
        const config = statusConfig[status] || { color: 'default', text: 'UNKNOWN' };
        return (
          <Tag color={config.color} icon={config.icon}>
            {config.text}
          </Tag>
        );
      },
    },
    {
      title: 'Created Date',
      dataIndex: 'createdOn',
      key: 'createdOn',
      render: (date) => (
        <Text type="secondary">
          {date ? new Date(date).toLocaleDateString() : 'N/A'}
        </Text>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Go to Organization">
            <Button
              type="primary"
              size="small"
              icon={<ArrowRightOutlined />}
              onClick={() => handleViewOrganization(record)}
            >
              View
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const filteredOrganizations = organizations.filter(org => {
    if (orgFilter === "all") return true;
    if (orgFilter === "pending") return org.status === 0;
    if (orgFilter === "approved") return org.status === 1;
    return true;
  });

  const courseColumns = [
    {
      title: 'Course',
      dataIndex: 'title',
      key: 'title',
      render: (text) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{text || 'No Title'}</div>
        </div>
      ),
    },
    {
  title: 'Cost',
  key: 'pricing',
  width: 100, 
  align: 'center',
  render: (_, record) => {
    const isPaid = record.isPaid;
    const price = record.pricing?.price;
    const currency =
      record.pricing?.currency === 'USD'
        ? '$'
        : record.pricing?.currency || '';

    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          fontSize: '14px',
          fontWeight: 500,
          whiteSpace: 'nowrap', 
        }}
      >
        {isPaid ? (
          <>
            <span style={{ marginRight: 2 }}>{currency}</span>
            <span>{Number(price).toFixed(1)}</span>
          </>
        ) : (
          'Free'
        )}
      </div>
    );
  },
},

    {
      title: 'Enrolled',
      dataIndex: 'enrollInprogressCount',
      key: 'enrolled',
      render: (_, record) => {
        const totalEnrolled = (record.enrollInprogressCount || 0) + (record.enrollCompletedCount || 0);
        return (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 'bold', fontSize: '16px' }}>{totalEnrolled}</div>
          </div>
        );
      },
    },
    {
      title: 'Completed',
      dataIndex: 'enrollCompletedCount',
      key: 'completed',
      render: (completedCount) => (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 'bold', fontSize: '16px' }}>{completedCount || 0}</div>
        </div>
      ),
    },
    {
      title: 'Completion Rate',
      key: 'completionRate',
      render: (_, record) => {
        const completed = record.enrollCompletedCount || 0;
        const inProgress = record.enrollInprogressCount || 0;
        const total = completed + inProgress;

        // Calculate completion rate using your formula: (a/(a+b))*100
        const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;

        return (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 'bold', fontSize: '16px', color: completionRate >= 50 ? '#52c41a' : '#faad14' }}>
              {completionRate}%
            </div>
            <Progress
              percent={completionRate}
              size="small"
              showInfo={false}
              strokeColor={completionRate >= 50 ? '#52c41a' : '#faad14'}
            />
          </div>
        );
      },
    },
    {
      title: 'Duration',
        responsive: ['md'],
      dataIndex: 'estimatedTime',
      key: 'estimatedTime',
      render: (time) => (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 'bold' }}>{time || '-'}</div>
        </div>
      ),
    },
  ];

  // Handle course pagination change
  const handleCoursePageChange = (page, size) => {
    setCourseCurrentPage(page);
    if (size !== coursePageSize) {
      setCoursePageSize(size);
      setCourseCurrentPage(1);
    }
  };

  // Handle enrollment pagination change
  const handleEnrollmentPageChange = (page, size) => {
    setEnrollmentCurrentPage(page);
    if (size !== enrollmentPageSize) {
      setEnrollmentPageSize(size);
      setEnrollmentCurrentPage(1);
    }
  };

  // Get current page data for courses
  const getCurrentCourseData = () => {
    const startIndex = (courseCurrentPage - 1) * coursePageSize;
    const endIndex = startIndex + coursePageSize;
    return safeData.courseLEnrollCountsList.slice(startIndex, endIndex);
  };

  // Get current page data for enrollments
  const getCurrentEnrollmentData = () => {
    const startIndex = (enrollmentCurrentPage - 1) * enrollmentPageSize;
    const endIndex = startIndex + enrollmentPageSize;
    return safeData.recentEnrollmentList.slice(startIndex, endIndex);
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
          backgroundColor: darkMode ? "#141414" : "#fff",
          transition: "background-color 0.3s ease",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <Spin
            indicator={spinnerIcon}
            tip="Loading your dashboard, please wait..."
            size="large"
            style={{
              display: "inline-block",
              marginBottom: 16,
            }}
          />
          <div
            style={{
              fontSize: "18px",
              color: darkMode ? "#fff" : "#000",
              fontWeight: 600,
              marginTop: "20px",
            }}
          >
            Please wait while we load your data.
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
          backgroundColor: darkMode ? "#141414" : "#fff",
          color: darkMode ? "#fff" : "#000",
          fontSize: 18,
          fontWeight: 500,
        }}
      >
        ⚠️ Error: {error}
      </div>
    );
  }

  const toggleDarkMode = () => setDarkMode(!darkMode);
  const styles = getStyles(darkMode);

  // Current page data for courses and enrollments
  const currentCourseData = getCurrentCourseData();
  console.log("curent courese", currentCourseData);
  const currentEnrollmentData = getCurrentEnrollmentData();

  return (
    <div style={styles.container}>
      <ProAdminHeader
        darkMode={darkMode}
        toggleDarkMode={toggleDarkMode}
      />

      {/* Main Dashboard Content */}
      <div style={{ padding: '24px' }}>

        {/* Overview Statistics */}
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={12} lg={6}>
            <Card className="card-professional">
              <Statistic
                title="Total Learners"
                value={safeData.learnerCount}
                prefix={<TeamOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {safeData.learnerMonthCount} + this month
              </Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className="card-professional">
              <Statistic
                title="Total Organizations"
                value={safeData.organizationCount}
                prefix={<BookOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
          { safeData.pendingOrgRequestCount > 0 &&  <Text type="secondary" style={{ fontSize: '12px', color: '#ff4d4f' }}>
                Pending Organizations {safeData.pendingOrgRequestCount}
              </Text>}   
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className="card-professional">
              <Statistic
                title="Available Courses"
                value={safeData.coursesCount}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: '#faad14' }}
              />
              <Progress
                percent={Math.round((safeData.enrollmentCount / Math.max(safeData.learnerCount, 1)) * 100)}
                size="small"
                showInfo={false}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className="card-professional">
              <Statistic
                title="Total Enrolled"
                value={safeData.enrollmentCount}
                suffix="%"
                prefix={<BarChartOutlined />}
                valueStyle={{ color: '#722ed1' }}
              />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {safeData.enrollmentMonthCount} + This month Enrolled
              </Text>
            </Card>
          </Col>
        </Row>

        {/* Organization Management Section */}
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col span={24}>
            <Card
              className="card-professional"
              title={
                <Row gutter={[8, 8]} align="middle">
                  <Col xs={24} sm={16}>
                    <Title level={4} style={{ margin: 0, color: "white" }}>
                      Organization Management
                    </Title>
                  </Col>
                  <Col xs={24} sm={16}>
                    <Space>
                      <Select
                        value={orgFilter}
                        onChange={setOrgFilter}
                        style={{ minWidth: 120, width: '100%'}}
                        size="small"
                      >
                        <Option value="all">All Organizations</Option>
                        <Option value="pending">Pending Approval</Option>
                        <Option value="approved">Approved</Option>
                      </Select>
                    </Space>
                  </Col>
                </Row>
              }
            >
              <Table
                columns={orgColumns}
                dataSource={filteredOrganizations}
                rowKey="id"
                loading={orgLoading}
                pagination={{ pageSize: 5 }}
                size="small"
                scroll={{ x: 800 }}
              />
            </Card>
          </Col>
        </Row>

        {/* Course Performance and Recent Enrollments */}
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} lg={16}>
            <Card
              title="Course Performance Overview"
              className="card-professional"
            >
              <Table
                dataSource={currentCourseData}
                columns={courseColumns}
                pagination={false}
                size="small"
                rowKey="id"
              />
              {/* Pagination for Course Performance */}
              {safeData.courseLEnrollCountsList.length > 0 && (
                <Row justify="end" style={{ marginTop: "16px" }}>
                  <Pagination
                    current={courseCurrentPage}
                    pageSize={coursePageSize}
                    total={safeData.courseLEnrollCountsList.length}
                    onChange={handleCoursePageChange}
                    showSizeChanger
                    showTotal={(total, range) =>
                      `${range[0]}-${range[1]} of ${total} courses`
                    }
                    pageSizeOptions={['3', '5', '10', '15']}
                  />
                </Row>
              )}
            </Card>
          </Col>
          <Col xs={24} lg={8}>
            <Card
              title="Recent Enrollments"
              className="card-professional"
            >
              {currentEnrollmentData && currentEnrollmentData.length > 0 ? (
                <>
                  <List
                    dataSource={currentEnrollmentData}
                    renderItem={item => (
                      <List.Item>
                        <List.Item.Meta
                          avatar={<Avatar icon={<UserOutlined />} />}
                          title={`${item.learner?.firstName || 'Unknown'} ${item.learner?.lastName || ''}`}
                          description={
                            <Space direction="vertical" size={0}>
                              <Text type="secondary">{item.course?.title || 'No Course Title'}</Text>
                              <Text type="secondary" style={{ fontSize: '12px' }}>
                                {item.createdOn ? new Date(item.createdOn).toLocaleDateString() : 'Unknown date'}
                              </Text>
                            </Space>
                          }
                        />
                        <Tag color={item.isCompleted ? 'green' : 'blue'}>
                          {item.isCompleted ? 'Completed' : 'In Progress'}
                        </Tag>
                      </List.Item>
                    )}
                  />
                  {/* Pagination for Recent Enrollments */}
                  <Row justify="end" style={{ marginTop: "16px" }}>
                    <Pagination
                      current={enrollmentCurrentPage}
                      pageSize={enrollmentPageSize}
                      total={safeData.recentEnrollmentList.length}
                      onChange={handleEnrollmentPageChange}
                      showSizeChanger
                      showTotal={(total, range) =>
                        `${range[0]}-${range[1]} of ${total} enrollments`
                      }
                      pageSizeOptions={['4', '7', '10']}
                      size="small"
                    />
                  </Row>
                </>
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No enrollment data available"
                />
              )}
            </Card>
          </Col>
        </Row>

        {/* Quick Stats Row */}
        <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
          <Col xs={24} sm={12}>
            <Card className="card-professional" size="small">
             <div
    style={{
      fontSize: 'clamp(18px, 4vw, 28px)',
      fontWeight: 600,
      color: '#52c41a',
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      marginTop: 8
    }}
  >
    <RiseOutlined />
    {safeData.onlineLearnerCount}
  </div>
            </Card>
          </Col>
          <Col xs={24} sm={12}>
            <Card className="card-professional" size="small">
              <div style={{
      fontSize: 'clamp(18px, 4vw, 28px)',
      fontWeight: 600,
       color: safeData.totalCertificates > 0 ? '#1890ff' : '#d9d9d9',
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      marginTop: 8
    }}>
             <CheckCircleOutlined />
              {safeData.totalCertificates}
 
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default ProAdminLandingPage;